// TODO(#69): Technical debt
// Currently, PubNub response values are not strictly typed.
// To improve type safety and maintainability, PubNub type definitions should be organized and addressed in the future.
import { patchChannelResponseStatus } from '../../../utils/channelResponseStatus';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { MessageEvent } from 'pubnub';
import { usePubNub } from 'pubnub-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  TChannel,
  getManagementChannels,
  updateSeenTime,
} from '../../../api/messages';
import {
  ChannelList,
  ChannelListHeader,
  Conversation,
} from '../../../components';
import { CHAT_CHANNELS_PAGE_SIZE } from '../../../constants';
import {
  isMessageFromChatUser,
  isUnrecordedChannelNotification,
  runSeenTimeSync,
} from '../../../utils/chatUnread';
import { dedupeById } from '../../../utils/dedupeById';
import { sidebarStrings, messageStrings } from '../../../constants/strings';
import { useAuth } from '../../../contexts/AuthContext';
import { useCurrentUser, useNotificationSound } from 'hooks';
import { useMe } from 'hooks/useMe';
import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context';
import { isOtherOrganizationPersonalChannel } from '../../../utils/personalChannels';
import { TOTAL_UNREAD_COUNT_QUERY_KEY } from 'hooks/useTotalUnreadCount';
import {
  getTrustedRecallMessageNotification,
  isIncomingChannelNotification,
  isMessageRecalledByPubNub,
} from '../../../utils/chatRecallMessage';
import { fetchAndApplyRecallStatusForLastMessages } from '../../../utils/fetchRecallStatusForLastMessages';
import {
  CHAT_LIST_HISTORY_FETCH_COUNT,
  buildChatListHistoryPage,
  buildChatListHydrationKey,
  compareChatListMessageTimetokens,
  findLatestVisibleChatListMessageWithPagination,
  isChatListMessageVisible,
  mergeHydratedChatListMessages,
  mergeLatestChatListMessages,
  retainVisibleChatListMessages,
} from '../../../utils/chatListMessage';

// Main Chat page component (requires PubNubProvider)
export default function ChatContent() {
  const { user } = useAuth();
  const pubnub = usePubNub();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [needsResponseOnly, setNeedsResponseOnly] = useState(false);
  const [search, setSearch] = useState<string | undefined>();

  // Get current user with UUID
  const { data: currentUser } = useCurrentUser();
  const { data: me } = useMe();
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const playNotificationSound = useNotificationSound();

  const channelId = useMemo(() => searchParams.get('channel'), [searchParams]);

  // Store last messages and unread counts
  const [lastMessageList, setLastMessageList] = useState<{
    [x: string]: {
      channel: string;
      message: any;
      timetoken: string | number;
      uuid?: string;
      recalled?: boolean;
    };
  }>({});
  const [unreadMessageList, setUnreadMessageList] = useState<{
    [x: string]: number;
  }>({});
  const lastMessageListRef = useRef(lastMessageList);
  lastMessageListRef.current = lastMessageList;

  // Fetch api
  const { data, isLoading, fetchNextPage, hasNextPage, error, refetch } =
    useInfiniteQuery({
      queryKey: ['management-channels', search, needsResponseOnly],
      queryFn: async ({ pageParam }) => {
        const result = await getManagementChannels({
          currentPage: pageParam,
          pageSize: CHAT_CHANNELS_PAGE_SIZE,
          search,
          responseStatus: needsResponseOnly ? 'NEEDS_RESPONSE' : undefined,
        });
        return {
          data: result.data,
          totalPage: Math.ceil(result.total / CHAT_CHANNELS_PAGE_SIZE),
        };
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        return lastPage?.totalPage > lastPageParam
          ? lastPageParam + 1
          : undefined;
      },
      staleTime: 1000,
      retry: 0,
      enabled: !!user,
    });

  // 重複排除前の取得済みチャンネル（InfiniteScroll の dataLength 用。ページ取得ごとに増える）
  const rawChannels = useMemo(
    () => data?.pages.flatMap(({ data }) => data) ?? [],
    [data],
  );

  // ページ境界で同一チャンネルが重複し得るため id で一意化する（表示用）
  const channels = useMemo(() => dedupeById(rawChannels), [rawChannels]);
  const channelsRef = useRef(channels);
  channelsRef.current = channels;
  // 一覧の再取得でunreadCountや並び順だけが変わっても全channelの履歴を取り直さない
  // よう、hydrateの再実行はchannel集合とtrust set（管理participant UUID）の変化に限る。
  const channelHydrationKey = useMemo(
    () => buildChatListHydrationKey(channels),
    [channels],
  );

  // Map message helper (for file messages and media array)
  const mapMessage = useCallback(
    (message: {
      channel: string;
      message: any;
      timetoken: string | number;
      uuid?: string;
      actions?: Record<string, Record<string, unknown>>;
      recalled?: boolean;
    }) => {
      let url;
      if (message.message?.file) {
        url = pubnub.getFileUrl({
          channel: message.channel,
          id: message.message.file.id,
          name: message.message.file.name,
        });
      } else if (
        message.message?.media &&
        Array.isArray(message.message.media) &&
        message.message.media.length > 0
      ) {
        url = message.message.media[0].url;
      }
      return {
        ...message,
        url,
        recalled:
          message.recalled ?? isMessageRecalledByPubNub(message.actions),
      };
    },
    [pubnub],
  );

  // Update unread count
  const updateUnread = useCallback(
    (channelId: string, reset: boolean) => {
      setUnreadMessageList((prev) => ({
        ...prev,
        [channelId]: reset
          ? 0
          : (prev[channelId] ??
            channels.find((channel) => channel.id === channelId)
              ?.unreadCount ??
            0) + 1,
      }));
    },
    [channels],
  );

  const refreshLastMessage = useCallback(
    async (targetChannelId: string) => {
      if (!pubnub) return;
      const targetChannel = channels.find(
        (channel) => channel.id === targetChannelId,
      );
      // 一覧未ロードchannelは管理publisherのtrust setを判定できない。
      // fail-openで挨拶をpreviewへ入れず、後続の一覧hydrateへ委ねる。
      if (!targetChannel) return;
      const previewAtRequestStart = lastMessageListRef.current[targetChannelId];

      try {
        const res = await pubnub.fetchMessages({
          channels: [targetChannelId],
          count: CHAT_LIST_HISTORY_FETCH_COUNT,
          includeUUID: true,
          includeMessageActions: true,
          stringifiedTimeToken: true,
        });
        const messages = res.channels[targetChannelId];
        const lastMsg =
          await findLatestVisibleChatListMessageWithPagination({
            initialPage: buildChatListHistoryPage(
              messages ?? [],
              res.more,
            ),
            managementParticipantUuids:
              targetChannel.managementParticipantUuids,
            fetchOlder: async (start) => {
              const older = await pubnub.fetchMessages({
                channels: [targetChannelId],
                count: CHAT_LIST_HISTORY_FETCH_COUNT,
                includeUUID: true,
                includeMessageActions: true,
                stringifiedTimeToken: true,
                start,
              });
              return buildChatListHistoryPage(
                older.channels[targetChannelId] ?? [],
                older.more,
              );
            },
          });

        const latestTargetChannel = channelsRef.current.find(
          (channel) => channel.id === targetChannelId,
        );
        // fetch中に検索・paging・API更新で一覧外になったchannelや、最新trustで
        // 一覧除外になった旧候補はstateへ戻さない。
        if (!latestTargetChannel) return;
        if (
          lastMsg &&
          !isChatListMessageVisible(
            { message: lastMsg.message, uuid: lastMsg.uuid },
            latestTargetChannel.managementParticipantUuids,
          )
        ) {
          setLastMessageList((prev) =>
            retainVisibleChatListMessages(prev, [latestTargetChannel]),
          );
          return;
        }

        if (lastMsg) {
          const candidate = mapMessage({
            channel: targetChannelId,
            message: lastMsg.message,
            timetoken: lastMsg.timetoken,
            uuid: lastMsg.uuid,
            actions: lastMsg.actions,
          });
          setLastMessageList((prev) => {
            const visiblePrev = retainVisibleChatListMessages(prev, [
              latestTargetChannel,
            ]);
            // UUID channelの通知timetokenは実messageより新しいため、recall
            // refreshだけはrequest開始時previewの参照CASで履歴候補へ置換する。
            if (prev[targetChannelId] !== previewAtRequestStart) {
              return visiblePrev;
            }
            return { ...visiblePrev, [targetChannelId]: candidate };
          });
          return;
        }

        setLastMessageList((prev) => {
          const visiblePrev = retainVisibleChatListMessages(prev, [
            latestTargetChannel,
          ]);
          // request中にrealtime/hydrateが更新したpreviewは削除しない。
          if (prev[targetChannelId] !== previewAtRequestStart) {
            return visiblePrev;
          }
          const next = { ...visiblePrev };
          delete next[targetChannelId];
          return next;
        });
      } catch (err) {
        console.error('Error refreshing last message after delete:', err);
      }
    },
    [pubnub, mapMessage, channels],
  );

  const refetchTotalUnreadCount = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: TOTAL_UNREAD_COUNT_QUERY_KEY });
  }, [queryClient]);

  // ローカルの未読上書きを捨て、行のバッジをサーバ値へ戻す
  const clearUnreadOverride = useCallback((targetChannelId: string) => {
    setUnreadMessageList((prev) => {
      if (prev[targetChannelId] === undefined) return prev;
      const next = { ...prev };
      delete next[targetChannelId];
      return next;
    });
  }, []);

  const syncSeenTime = useCallback(
    (targetChannelId: string) => {
      void runSeenTimeSync({
        channelId: targetChannelId,
        updateSeenTime: (id) => updateSeenTime(id, organizationId),
        onFailure: clearUnreadOverride,
        onSettled: refetchTotalUnreadCount,
      });
    },
    [clearUnreadOverride, refetchTotalUnreadCount, organizationId],
  );

  // Refresh the channel list preview/unread after a recall. Shared by the trusted
  // RECALL_MESSAGE PubNub handler and the local confirm handler so the sidebar
  // updates even when the PubNub notification fails or is delayed.
  const handleRecallRefresh = useCallback(
    (targetChannelId: string) => {
      void refreshLastMessage(targetChannelId);
      void refetch().finally(() => {
        clearUnreadOverride(targetChannelId);
      });
      refetchTotalUnreadCount();
    },
    [refreshLastMessage, refetch, clearUnreadOverride, refetchTotalUnreadCount],
  );

  // Handle incoming messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // AppLayout は全所属の個人チャネルを同じクライアントで購読するため、別組織の通知は無視する
      if (
        isOtherOrganizationPersonalChannel(
          me?.memberships,
          event.channel,
          organizationId,
        )
      ) {
        return;
      }
      const recallNotification = getTrustedRecallMessageNotification(
        event.message,
        event.publisher,
      );
      if (recallNotification?.channel) {
        // The server unread count already excludes recalled messages. Refetch the
        // authoritative count, then drop the optimistic local override so the
        // channel badge reflects the server value. This handles non-last and
        // active-channel recalls that a local decrement-by-one cannot track.
        handleRecallRefresh(recallNotification.channel);
        return;
      }

      if (!isIncomingChannelNotification(event.message)) return;

      const channelId = event.message.channel;
      const isMyMessage = currentUser?.uuid === event.publisher;
      const managementParticipantUuids =
        channels.find((channel) => channel.id === channelId)
          ?.managementParticipantUuids ?? [];
      if (
        !isChatListMessageVisible(
          {
            message: event.message,
            uuid: event.publisher,
          },
          managementParticipantUuids,
        )
      ) {
        return;
      }

      const candidate = mapMessage({
        channel: channelId,
        message: event.message,
        timetoken: event.timetoken,
        uuid: event.publisher,
      });
      setLastMessageList((prev) =>
        mergeLatestChatListMessages(prev, { [channelId]: candidate }),
      );

      const activeChannelId = searchParams.get('channel');

      // The open conversation can be outside the current search/status filter.
      const knownChannel =
        channels.find((row) => row.id === channelId) ??
        queryClient.getQueryData<TChannel>(['channel-detail', 'male', channelId]);
      const isFromChatUser = isMessageFromChatUser(
        knownChannel ? [knownChannel] : [],
        channelId,
        event.publisher,
      );
      // Other administrators also publish automated messages: do not infer a
      // response from those. Our own successful sends patch in Chat.onSuccess.
      if (isFromChatUser && !isUnrecordedChannelNotification(event.message)) {
        patchChannelResponseStatus(queryClient, channelId, 'NEEDS_RESPONSE');
      }

      // 一覧に未ロードのチャンネル（検索絞り込み・ページング圏外・新規フォロー直後）は
      // 参加者情報が無く送信者種別を判定できない。表示中なら閲覧＝既読として
      // 同期だけ行い、それ以外は従来どおり自分以外の送信で通知音のみ鳴らす。
      // 件数は行が表示されるときのAPI再取得に任せる
      const isChannelLoaded = channels.some(
        (channel) => channel.id === channelId,
      );
      if (!isChannelLoaded) {
        if (channelId === activeChannelId) {
          syncSeenTime(channelId);
          return;
        }
        if (!isMyMessage) playNotificationSound();
        return;
      }

      if (!isFromChatUser) {
        // 自分が表示中のチャンネルへ送ったときだけ、サーバの既読時刻を進める
        if (isMyMessage && channelId === activeChannelId) {
          syncSeenTime(channelId);
        }
        return;
      }

      // 表示中チャンネルへの新着は未読を増やさず既読扱いにする。
      // アクティブチャンネルの新着はサーバの既読時刻も同期する（InquiryChat と同じ挙動）。
      if (channelId === activeChannelId) {
        updateUnread(channelId, true);
        // 総未読の再取得は既読の記録が終わってから。UnreadCountInvalidator の
        // デバウンス invalidate が先に走ると、サイドバー総数が水増しのまま残る
        syncSeenTime(channelId);
        return;
      }

      // 非アクティブチャンネルへの利用者からの新着: 通知音 + 未読 +1。
      // ただし台帳へ記録できなかった送信はサーバの合計に入らないため、
      // 件数は動かさず通知音だけにする（増やすと合計と食い違ったまま戻らない）
      playNotificationSound();
      if (isUnrecordedChannelNotification(event.message)) return;
      updateUnread(channelId, false);
    },
    [
      mapMessage,
      updateUnread,
      channels,
      currentUser,
      playNotificationSound,
      searchParams,
      syncSeenTime,
      handleRecallRefresh,
      queryClient,
      me?.memberships,
      organizationId,
    ],
  );

  // Fetch last messages on mount
  useEffect(() => {
    // 依存キーが変わったrenderのchannelsを使う（同一集合ならeffect自体が走らない）。
    const currentChannels = channelsRef.current;
    if (!pubnub || !currentChannels.length) return;
    let cancelled = false;
    // trust更新はnetwork成否を待たず、過去にfail-openしたmarkerへ即時反映する。
    setLastMessageList((prev) =>
      retainVisibleChatListMessages(prev, currentChannels),
    );

    const fetchLastMessages = async () => {
      const previewsAtRequestStart = lastMessageListRef.current;
      try {
        // NOTE: includeMessageActions is unsupported when fetching multiple
        // channels at once — PubNub throws "History can return actions data for a
        // single channel only." Requesting it here makes the whole call reject and
        // leaves every channel's preview blank on (re)load. Recall state for the
        // list is handled live via RECALL_MESSAGE events and the single-channel
        // refreshLastMessage instead.
        const res = await pubnub.fetchMessages({
          channels: currentChannels.map(({ id }) => id),
          count: CHAT_LIST_HISTORY_FETCH_COUNT,
          includeUUID: true,
          stringifiedTimeToken: true,
        });

        const channelResults = await Promise.allSettled(
          currentChannels.map(async (channel) => {
            const lastMsg =
              await findLatestVisibleChatListMessageWithPagination({
                initialPage: {
                  records: res.channels[channel.id] ?? [],
                },
                managementParticipantUuids:
                  channel.managementParticipantUuids,
                fetchOlder: async (start) => {
                  if (cancelled) return { records: [] };
                  const older = await pubnub.fetchMessages({
                    channels: [channel.id],
                    count: CHAT_LIST_HISTORY_FETCH_COUNT,
                    includeUUID: true,
                    stringifiedTimeToken: true,
                    start,
                  });
                  return buildChatListHistoryPage(
                    older.channels[channel.id] ?? [],
                    older.more,
                  );
                },
              });
            if (!lastMsg) return null;

            return [
              channel.id,
              mapMessage({
                channel: channel.id,
                message: lastMsg.message,
                timetoken: lastMsg.timetoken,
                uuid: lastMsg.uuid,
              }),
            ] as const;
          }),
        );

        const newState: typeof lastMessageList = {};
        const emptyChannelIds: string[] = [];
        channelResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            if (result.value) {
              const [channelId, message] = result.value;
              newState[channelId] = message;
            } else {
              emptyChannelIds.push(currentChannels[index].id);
            }
            return;
          }
          if (!cancelled) {
            console.error(
              `Error fetching last message for channel ${currentChannels[index].id}:`,
              result.reason,
            );
          }
        });

        if (cancelled) return;
        setLastMessageList((prev) => {
          const visiblePrev = retainVisibleChatListMessages(
            prev,
            currentChannels,
          );
          return mergeHydratedChatListMessages({
            current: visiblePrev,
            candidates: newState,
            emptyChannelIds,
            previewsAtRequestStart,
          });
        });

        await fetchAndApplyRecallStatusForLastMessages(
          newState,
          (update) => {
            if (!cancelled) setLastMessageList(update);
          },
        );
      } catch (err) {
        if (!cancelled) console.error('Error fetching last messages:', err);
      }
    };

    void fetchLastMessages();
    return () => {
      cancelled = true;
    };
  }, [pubnub, channelHydrationKey, mapMessage]);

  // Create channelsView with last messages
  const channelsView = useMemo(() => {
    const enrichedChannels = [...channels].map((channel) => {
      const lastMessage = lastMessageList[channel.id];
      return {
        ...channel,
        // effect cleanup前のrenderでも、最新trustでhiddenなら描画しない。
        lastMessage:
          lastMessage &&
          isChatListMessageVisible(
            lastMessage,
            channel.managementParticipantUuids,
          )
            ? lastMessage
            : undefined,
      };
    });

    return enrichedChannels.sort((a, b) => {
      if (
        a.lastMessage?.timetoken != null &&
        b.lastMessage?.timetoken != null
      ) {
        const exactComparison = compareChatListMessageTimetokens(
          a.lastMessage.timetoken,
          b.lastMessage.timetoken,
        );
        if (exactComparison != null) return -exactComparison;
      }
      const aTime = a.lastMessage?.timetoken
        ? Number(a.lastMessage.timetoken) / 10000
        : a.lastMessageTime
          ? moment(a.lastMessageTime).valueOf()
          : 0;
      const bTime = b.lastMessage?.timetoken
        ? Number(b.lastMessage.timetoken) / 10000
        : b.lastMessageTime
          ? moment(b.lastMessageTime).valueOf()
          : 0;
      return bTime - aTime;
    });
  }, [channels, lastMessageList]);

  // Use ref to always have the latest handleMessage callback
  const handleMessageRef = useRef(handleMessage);
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // Memoize channel IDs to avoid unnecessary re-subscriptions
  const channelIds = useMemo(
    () => channelsView.map((channel) => channel?.id).filter(Boolean),
    [channelsView],
  );
  const userNotificationChannel = currentUser?.uuid;
  const pubnubSubscriptionChannels = useMemo(() => {
    if (!userNotificationChannel) return channelIds;
    return channelIds.includes(userNotificationChannel)
      ? channelIds
      : [...channelIds, userNotificationChannel];
  }, [channelIds, userNotificationChannel]);
  const channelIdsRef = useRef(channelIds);
  const pubnubSubscriptionChannelsRef = useRef(pubnubSubscriptionChannels);
  channelIdsRef.current = channelIds;
  pubnubSubscriptionChannelsRef.current = pubnubSubscriptionChannels;
  const channelIdsKey = channelIds.join(',');
  const pubnubSubscriptionChannelsKey = pubnubSubscriptionChannels.join(',');

  // Subscribe to chat channels and the admin UUID channel (DELETE_MESSAGE notifications).
  useEffect(() => {
    const currentChannelIds = channelIdsRef.current;
    const currentSubscriptionChannels = pubnubSubscriptionChannelsRef.current;
    if (!pubnub || !currentSubscriptionChannels.length) {
      return;
    }

    const listenerParams = {
      message: (event: MessageEvent) => {
        handleMessageRef.current(event);
      },
    };

    pubnub.subscribe({ channels: currentSubscriptionChannels });
    pubnub.addListener(listenerParams);

    return () => {
      // UUID channel may be shared with AppLayout; only tear down chat channel subscriptions.
      if (currentChannelIds.length > 0) {
        pubnub.unsubscribe({ channels: currentChannelIds });
      }
      pubnub.removeListener(listenerParams);
    };
  }, [pubnub, pubnubSubscriptionChannelsKey, channelIdsKey]);

  const getUnreadCount = useCallback(
    (item: TChannel & { lastMessage?: any }) => {
      // サーバ件数は台帳方式で管理側の送信を含まない。旧方式の名残だった
      // 「最後の送信が自分なら0」の補正は、既読同期が失敗したときに
      // 実在する未読を行だけ隠し、サイドバー総数と食い違う原因になるため置かない
      return unreadMessageList[item.id] ?? item.unreadCount ?? 0;
    },
    [unreadMessageList],
  );

  const onChangeChannel = useCallback(
    (id: string) => {
      const channel = channelsView.find((c) => c.id === id);
      if (!channel) {
        setSearchParams({ channel: id });
        return;
      }
      const unreadBeforeSelect = getUnreadCount(channel);

      setSearchParams({ channel: id });
      setUnreadMessageList((prev) => ({
        ...prev,
        [id]: 0,
      }));

      if (unreadBeforeSelect === 0) {
        return;
      }

      syncSeenTime(id);
    },
    [setSearchParams, channelsView, getUnreadCount, syncSeenTime],
  );

  // モバイルの会話画面から一覧へ戻る。アクティブ中に受信した分の未読を確実にクリアする
  const onBackToList = useCallback(() => {
    if (!channelId) return;

    const channel = channelsView.find((c) => c.id === channelId);
    const unreadBeforeBack = channel ? getUnreadCount(channel) : 0;

    setSearchParams({});
    setUnreadMessageList((prev) => ({
      ...prev,
      [channelId]: 0,
    }));

    if (unreadBeforeBack === 0) {
      return;
    }

    syncSeenTime(channelId);
  }, [channelId, channelsView, getUnreadCount, setSearchParams, syncSeenTime]);

  return (
    <section className="chat-section-h chat-fullbleed rounded-none border-0 lg:rounded-[28px] lg:border border-white/70 bg-white/85 shadow-soft backdrop-blur-sm overflow-hidden">
      <div className="h-full flex">
        <div
          className={`${
            channelId ? 'hidden md:flex' : 'flex'
          } h-full basis-full md:basis-2/5 lg:basis-1/4 md:border-r border-neutral-200 flex-col bg-transparent`}
        >
          <div className="flex-shrink-0">
            <ChannelListHeader
              title={sidebarStrings.navLabels.chatMale}
              onSearch={setSearch}
              unreadCount={0}
            >
              <button
                type="button"
                aria-pressed={needsResponseOnly}
                onClick={() => setNeedsResponseOnly((value) => !value)}
                className={`mt-3 rounded-[var(--btn-radius)] border px-3 py-1 text-sm ${
                  needsResponseOnly
                    ? 'border-red-300 bg-red-50 text-red-700'
                    : 'border-neutral-300 text-neutral-600'
                }`}
              >
                {messageStrings.responseStatus.needsResponseOnly}
              </button>
            </ChannelListHeader>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <ChannelList
              channels={channelsView}
              dataLength={rawChannels.length}
              fetchMore={fetchNextPage}
              hasMore={hasNextPage || false}
              onSelectChannel={onChangeChannel}
              currentChannel={channelId}
              isLoading={isLoading}
              errorMsg={error?.message}
              refetch={refetch}
              lastMessageList={lastMessageList}
              unreadMessageList={unreadMessageList}
              getUnreadCount={getUnreadCount}
            />
          </div>
        </div>
        <div
          className={`${
            channelId ? '' : 'hidden md:block'
          } flex-1 bg-transparent overflow-hidden`}
        >
          {channelId && (
            <Conversation
              key={channelId}
              channelId={channelId}
              loadingChannel={isLoading}
              onBack={onBackToList}
              onMessageRecalled={handleRecallRefresh}
            />
          )}
        </div>
      </div>
    </section>
  );
}
